import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Zap, Lock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. Token may be expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F0F4F8] to-[#E6EFF8] flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-white border border-slate-200 shadow-lg rounded-xl">
          <CardContent className="p-8 text-center">
            <p className="text-slate-600 mb-4">Invalid reset link. Please request a new one.</p>
            <Link to="/forgot-password">
              <Button className="bg-[#003087] text-white hover:bg-[#002366]">Request Reset</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F4F8] to-[#E6EFF8] flex items-center justify-center p-6">
      <Card className="w-full max-w-md bg-white border border-slate-200 shadow-lg rounded-xl animate-fade-in" data-testid="reset-password-card">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="inline-flex items-center gap-2 justify-center mb-4">
            <div className="w-8 h-8 rounded-md bg-[#003087] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>BandBoost AI</span>
          </Link>
          <CardTitle className="text-2xl text-slate-900" style={{ fontFamily: "Public Sans" }}>
            {success ? "Password Reset" : "Set New Password"}
          </CardTitle>
          <CardDescription className="text-slate-500">
            {success ? "Your password has been changed" : "Enter your new password below"}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 pt-2">
          {success ? (
            <div className="text-center py-4 space-y-4">
              <CheckCircle className="w-12 h-12 text-[#007F3B] mx-auto" />
              <p className="text-sm text-slate-600">Your password has been reset. Please sign in with your new password.</p>
              <Link to="/login">
                <Button className="bg-[#003087] text-white hover:bg-[#002366]" data-testid="go-to-login-btn">Sign In</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">New Password</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Min 6 characters"
                    className="pl-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="reset-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Repeat password"
                    className="pl-10"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="reset-confirm-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#003087] text-white hover:bg-[#002366]"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
